/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import {
  Country,
  formatPhoneNumberIntl,
  isValidPhoneNumber,
} from 'react-phone-number-input';
import { Select, ToggleInput, Input, PhoneNumberInput } from '../../..';
import { useAuth, User } from '../../../../context/authContext';
import useValidator from '../../../../hooks/useValidator';
import { PrefFormActions } from '../../../../pages/Preferences/Preferences';
import apigateway from '../../../../utils/apigateway';
import { businessTypes, industries } from '../../../../utils/businessTypes';
import countries from '../../../../utils/countries';
import setCookie from '../../../../utils/setCookie';
import setToLocalStorage from '../../../../utils/setToLocalStorage';
import timezones from '../../../../utils/timezones';

export type StoreFormDataType = {
  id?: number;
  name?: string;
  displayName: string;
  storeUrl?: string;
  businessType: string;
  industry: string;
  countryCode: string;
  timezone: string;
  taxId: string;
  email: string;
  phoneNumber: string;
  website: string;
  language: string;
  dateFormat: string;
  inActiveTimer: string;
  outOfStockAlert: boolean;
  productSoldAlert: boolean;
  address: string;
  otherAddress: string;
  city: string;
  state: string;
  postalCode: string;
};

type GeneralTabProps = {
  user: User;
};

const customValidators = {
  phone: {
    message: 'The phone must be a valid international number (e.g. +233).',
    rule: (val: string) => {
      return isValidPhoneNumber(val);
    },
    required: false,
  },
};

const GeneralTab = React.forwardRef<PrefFormActions, GeneralTabProps>(
  ({ user }, ref) => {
    const [, dispatch] = useAuth();
    const [validator, showValidationMessage] = useValidator(
      {},
      customValidators,
    );
    const [storeAccount, setStoreAccount] = React.useState<StoreFormDataType>({
      name: user.defaultStore.name || '',
      displayName: user.defaultStore.displayName || '',
      storeUrl: user.defaultStore.storeUrl || '',
      businessType: user.defaultStore.businessType || '',
      industry: user.defaultStore.industry || '',
      countryCode: user.defaultStore.country.iso || 'GH',
      timezone: user.defaultStore.timezone || '',
      taxId: user.defaultStore.taxId || '',
      email: user.defaultStore.email || '',
      phoneNumber: user.defaultStore.phoneNumber || '',
      website: user.defaultStore.website || '',
      language: user.defaultStore.metadata?.language || '',
      dateFormat: user.defaultStore.metadata?.dateFormat || '',
      inActiveTimer: user.defaultStore.metadata?.inActiveTimer || '',
      outOfStockAlert:
        user.defaultStore.metadata?.notifications?.outOfStockAlert || false,
      productSoldAlert:
        user.defaultStore.metadata?.notifications?.productSoldAlert || false,
      address: user.defaultStore.address || '',
      otherAddress: user.defaultStore.otherAddress || '',
      city: user.defaultStore.city || '',
      state: user.defaultStore.state || '',
      postalCode: user.defaultStore.postalCode || '',
    });
    const [errors, setErrors] = React.useState({
      name: false,
      displayName: false,
      businessType: false,
      industry: false,
      email: false,
      phoneNumber: false,
      countryCode: false,
    });

    React.useImperativeHandle(ref, () => {
      return {
        fnActionCall: handleUpdateStore,
        fnShowModalCall: () => false,
      };
    });

    const handleInputChange = (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = event.target;

      setStoreAccount(fndStore => ({
        ...fndStore,
        [name]: value,
      }));
    };

    const handleUpdateStore = async () => {
      if (!validator.allValid()) {
        setErrors(errs => ({
          ...errs,
          name: !validator.fieldValid('name'),
          displayName: !validator.fieldValid('displayName'),
          businessType: !validator.fieldValid('businessType'),
          industry: !validator.fieldValid('industry'),
          email: !validator.fieldValid('email'),
          phoneNumber: !validator.fieldValid('phoneNumber'),
          countryCode: !validator.fieldValid('countryCode'),
        }));
        showValidationMessage(true);
        return;
      }

      const fndAccount = {
        ...storeAccount,
        phoneNumber: formatPhoneNumberIntl(storeAccount.phoneNumber).replace(
          /[^0-9+]/g,
          '',
        ),
      } as StoreFormDataType;

      delete fndAccount.name;
      delete fndAccount.storeUrl;

      const { store } = (await apigateway.put(
        `/store?store=${user.defaultStore.id}`,
        {
          ...fndAccount,
          storeId: user.defaultStore.id,
        },
      )) as any;

      const updatedUser: User = {
        ...user,
        defaultStore: store,
      };

      dispatch({ type: 'SET_AUTH_USER', payload: updatedUser });

      setToLocalStorage('_pos_usrid', JSON.stringify(updatedUser));

      await setCookie('_pos_store', JSON.stringify(store));
    };

    return (
      <div className="w-full mt-6">
        <div>
          <h2 className="text-xbase font-semibold">Store Details</h2>
          <div className="flex flex-row space-x-10 items-start">
            <div className="w-[60%]">
              <div className="pt-3 pb-5 space-y-4">
                <div>
                  <label htmlFor="name" className="text-xbase block mb-1">
                    Name{' '}
                    <span className="text-xs font-light text-pos-red">*</span>
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={storeAccount.name || ''}
                    onChange={handleInputChange}
                    onBlur={() => {
                      setErrors(errs => ({
                        ...errs,
                        name: !validator.check(
                          storeAccount.name,
                          'required|min:2',
                        ),
                      }));
                    }}
                    error={errors.name}
                    disabled
                  />
                  {validator.message(
                    'name',
                    storeAccount.name,
                    'required|min:2',
                    {
                      messages: {
                        required: 'Store name is required.',
                      },
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    },
                  )}
                </div>

                <div className="flex flex-row gap-5">
                  <div className="w-full">
                    <label
                      htmlFor="displayName"
                      className="relative text-xbase block mb-1"
                    >
                      Display Name As{' '}
                      {/* Shown on sales forms and purchase orders. */}
                      <span className="text-xs font-light text-pos-red">*</span>
                      <span
                        className="absolute leading-0 text-[10px] w-3 h-3 bg-[#666] rounded-full inline-flex
                      items-center justify-center text-white ml-1 cursor-help top-[5px]"
                      >
                        ?
                      </span>{' '}
                    </label>
                    <Input
                      id="displayName"
                      name="displayName"
                      value={storeAccount.displayName}
                      onChange={handleInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          displayName: !validator.check(
                            storeAccount.displayName,
                            'required|min:2',
                          ),
                        }));
                      }}
                      error={errors.displayName}
                    />
                    {validator.message(
                      'displayName',
                      storeAccount.displayName,
                      'required|min:2',
                      {
                        messages: {
                          required: 'Display name is required.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>

                  <div className="w-full">
                    <label htmlFor="storeUrl" className="text-xbase block mb-1">
                      Store URL
                    </label>
                    <Input
                      id="storeUrl"
                      name="storeUrl"
                      value={(storeAccount.storeUrl || '')
                        .replace(/^https?:\/\//, '')
                        .replace('www.', '')}
                      onChange={() => false}
                      onBlur={() => false}
                      error={false}
                      disabled
                    />
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className="w-full">
                    <label
                      htmlFor="businessType"
                      className="text-xbase block mb-1"
                    >
                      Type of Business{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: 'What do you sell?',
                      }}
                      value={storeAccount.businessType}
                      options={businessTypes.map(fndBusiness => ({
                        value: fndBusiness,
                        label: fndBusiness,
                      }))}
                      name="businessType"
                      id="businessType"
                      onChange={handleInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          businessType: !validator.check(
                            storeAccount.businessType,
                            'required',
                          ),
                        }));
                      }}
                      error={errors.businessType}
                    />
                    {validator.message(
                      'businessType',
                      storeAccount.businessType,
                      'required',
                      {
                        messages: {
                          required: 'Select your type of business.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>

                  <div className="w-full">
                    <label htmlFor="industry" className="text-xbase block mb-1">
                      Industry{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: 'What industry fits your business?',
                      }}
                      value={storeAccount.industry}
                      options={industries.map(fndIndustry => ({
                        value: fndIndustry,
                        label: fndIndustry,
                      }))}
                      name="industry"
                      id="industry"
                      onChange={handleInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          industry: !validator.check(
                            storeAccount.industry,
                            'required',
                          ),
                        }));
                      }}
                      error={errors.industry}
                    />
                    {validator.message(
                      'industry',
                      storeAccount.industry,
                      'required',
                      {
                        messages: {
                          required:
                            'Select the industry that best fits business.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className="w-full">
                    <label
                      htmlFor="countryCode"
                      className="text-xbase block mb-1"
                    >
                      Country{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: '',
                      }}
                      value={storeAccount.countryCode}
                      options={countries.map(fndCountry => ({
                        value: fndCountry.iso,
                        label: fndCountry.name,
                      }))}
                      name="countryCode"
                      id="countryCode"
                      onChange={handleInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          countryCode: !validator.check(
                            storeAccount.countryCode,
                            'required',
                          ),
                        }));
                      }}
                      error={errors.countryCode}
                    />
                    {validator.message(
                      'countryCode',
                      storeAccount.countryCode,
                      'required',
                      {
                        messages: {
                          required: 'Select your country.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                    <span className="text-pos-gray font-light text-sm mt-2 inline-block leading-normal">
                      NB: Store&apos;s currency will be based on selected
                      country.
                    </span>
                  </div>

                  <div className="w-full">
                    <label htmlFor="timezone" className="text-xbase block mb-1">
                      Timezone
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: '',
                      }}
                      options={timezones.map((fndTimezone: any, index) => {
                        const timezone = Object.keys(fndTimezone)[0];
                        return {
                          [timezone]: fndTimezone[timezone].map(
                            (fndZone: string) => ({
                              value: fndZone,
                              label: `(${timezone}) ${fndZone}`,
                            }),
                          ),
                        };
                      })}
                      value={storeAccount.timezone}
                      name="timezone"
                      id="timezone"
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="w-[50%]">
                  <label
                    htmlFor="taxId"
                    className="relative text-xbase block mb-1"
                  >
                    Tax ID
                  </label>
                  <Input
                    id="taxId"
                    name="taxId"
                    value={storeAccount.taxId}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Store's Contact Field */}
                <div className="space-y-4 !mt-6">
                  <div className="border-b border-[#d0d7de]">
                    <h2 className="text-xbase font-semibold inline-flex items-center">
                      Contact info
                    </h2>
                  </div>
                  <div className="flex flex-row gap-5">
                    <div className="w-full">
                      <label htmlFor="email" className="text-xbase block mb-1">
                        Email{' '}
                        <span className="text-xs font-light text-pos-red">
                          *
                        </span>
                      </label>
                      <Input
                        id="email"
                        name="email"
                        autoComplete="username"
                        value={storeAccount.email}
                        type="email"
                        onChange={handleInputChange}
                        onBlur={() => {
                          setErrors(errs => ({
                            ...errs,
                            email: !validator.check(
                              storeAccount.email,
                              'required|email',
                            ),
                          }));
                        }}
                        error={errors.email}
                      />
                      {validator.message(
                        'email',
                        storeAccount.email,
                        'required|email',
                        {
                          messages: {
                            required: 'Email address is required.',
                          },
                          className:
                            'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                        },
                      )}
                    </div>

                    <div className="w-full">
                      <label
                        htmlFor="phoneNumber"
                        className="text-xbase block mb-1"
                      >
                        Phone
                      </label>
                      <PhoneNumberInput
                        id="phoneNumber"
                        name="phoneNumber"
                        defaultCountry={storeAccount.countryCode as Country}
                        value={storeAccount.phoneNumber}
                        onChange={value =>
                          setStoreAccount(fndAccount => ({
                            ...fndAccount,
                            phoneNumber: value,
                          }))
                        }
                        onBlur={() => {
                          setErrors(errs => ({
                            ...errs,
                            phoneNumber: !validator.check(
                              storeAccount.phoneNumber,
                              'phone',
                            ),
                          }));
                        }}
                        error={errors.phoneNumber}
                      />
                      {validator.message(
                        'phoneNumber',
                        storeAccount.phoneNumber,
                        'phone',
                        {
                          className:
                            'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                        },
                      )}
                    </div>
                  </div>
                  {/* The website must be a url. */}
                  <div className="w-[50%]">
                    <label htmlFor="website" className="text-xbase block mb-1">
                      Website
                    </label>
                    <Input
                      id="website"
                      name="website"
                      type="url"
                      value={storeAccount.website}
                      onChange={handleInputChange}
                    />
                    {validator.message('website', storeAccount.website, 'url', {
                      messages: {
                        url: 'The website must be a url. (eg: https://retailshop.com)',
                      },
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    })}
                  </div>
                </div>

                {/* Store's Other Preferences Field */}
                <div className="space-y-4 !mt-6">
                  <div className="border-b border-[#d0d7de]">
                    <h2 className="text-xbase font-semibold inline-flex items-center">
                      Other Preferences
                    </h2>
                  </div>
                  <div className="flex flex-row gap-5">
                    <div className="w-full">
                      <label
                        htmlFor="language"
                        className="text-xbase block mb-1"
                      >
                        Language
                      </label>
                      <Select
                        placeholder={{
                          value: '',
                          label: '',
                        }}
                        options={[
                          {
                            value: 'en',
                            label: 'English',
                          },
                        ]}
                        id="language"
                        name="language"
                        value={storeAccount.language}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="w-full">
                      <label
                        htmlFor="dateFormat"
                        className="text-xbase block mb-1"
                      >
                        Date format
                      </label>
                      <Select
                        placeholder={{
                          value: '',
                          label: '',
                        }}
                        options={[
                          {
                            value: 'DD-MM-YYYY',
                            label: '18-05-1994 (DD-MM-YYYY)',
                          },
                          {
                            value: 'MM-DD-YYYY',
                            label: '05-18-1994 (MM-DD-YYYY)',
                          },
                          {
                            value: 'DD-MMM-YYYY',
                            label: '18-May-1994 (DD-MMM-YYYY)',
                          },
                          {
                            value: 'MMM-DD-YYYY',
                            label: 'May-18-1994 (MMM-DD-YYYY)',
                          },
                        ]}
                        id="dateFormat"
                        name="dateFormat"
                        value={storeAccount.dateFormat}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="w-[50%]">
                    <label
                      htmlFor="inActiveTimer"
                      className="text-xbase block mb-1"
                    >
                      Sign out when inactive for
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: '',
                      }}
                      options={[
                        {
                          value: 30,
                          label: '30 minutes',
                        },
                        {
                          value: 60,
                          label: '1 hour',
                        },
                        {
                          value: 60 + 60,
                          label: '2 hours',
                        },
                      ]}
                      id="inActiveTimer"
                      name="inActiveTimer"
                      value={storeAccount.inActiveTimer}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="space-y-7 !mt-8">
                    <div className="flex flex-row space-x-4 items-center w-[40%]">
                      <label
                        htmlFor="outOfStockAlert"
                        className="text-xbase flex flex-1"
                      >
                        Out of Stock Warning
                      </label>
                      <ToggleInput
                        id="outOfStockAlert"
                        name="outOfStockAlert"
                        value={storeAccount.outOfStockAlert.toString()}
                        checked={storeAccount.outOfStockAlert}
                        onChange={event =>
                          setStoreAccount(fndAccount => ({
                            ...fndAccount,
                            outOfStockAlert: event.target.checked,
                          }))
                        }
                        className="align-middle cursor-pointer shrink-0"
                      />
                    </div>

                    <div className="flex flex-row space-x-4 items-center w-[40%]">
                      <label
                        htmlFor="productSoldAlert"
                        className="text-xbase flex flex-1"
                      >
                        Product Sold Alert
                      </label>
                      <ToggleInput
                        id="productSoldAlert"
                        name="productSoldAlert"
                        value={storeAccount.productSoldAlert.toString()}
                        checked={storeAccount.productSoldAlert}
                        onChange={event =>
                          setStoreAccount(fndAccount => ({
                            ...fndAccount,
                            productSoldAlert: event.target.checked,
                          }))
                        }
                        className="align-middle cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-[40%] border border-[rgba(143,115,25,0.4)] rounded-md">
              <div className="pt-3 pb-5 px-5 space-y-4">
                <div className="w-full">
                  <label htmlFor="address" className="text-xbase block mb-1">
                    Address
                  </label>
                  <Input
                    name="address"
                    id="address"
                    value={storeAccount.address}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="w-full">
                  <label
                    htmlFor="otherAddress"
                    className="text-xbase block mb-1"
                  >
                    Other Address
                  </label>
                  <Input
                    id="otherAddress"
                    name="otherAddress"
                    value={storeAccount.otherAddress}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="city" className="text-xbase block mb-1">
                    City
                  </label>
                  <Input
                    name="city"
                    id="city"
                    value={storeAccount.city}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="flex flex-row gap-5">
                  <div className="w-[60%]">
                    <label htmlFor="state" className="text-xbase block mb-1">
                      State / Region
                    </label>
                    <Input
                      name="state"
                      id="state"
                      value={storeAccount.state}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="w-[40%]">
                    <label
                      htmlFor="postalCode"
                      className="text-xbase block mb-1"
                    >
                      Postal code
                    </label>
                    <Input
                      name="postalCode"
                      id="postalCode"
                      value={storeAccount.postalCode}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

// function GeneralTab({ info }: GeneralTabProps)

export default GeneralTab;
